import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  User, Sparkles, Link2, Save, Trash2, CircleCheckBig, Eye, EyeOff,
  Shield, Users, Database, KeyRound, Copy, Download, AlertTriangle,
  UserPlus, X, Palette, Loader2,
} from "lucide-react";
import { api, fmtErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";

const Loader2Icon = () => <Loader2 size={12} className="animate-spin" />;

const PROVIDERS = [
  { key: "anthropic", label: "Anthropic Claude", model_default: "claude-3-5-sonnet-latest", get_key_url: "https://console.anthropic.com/settings/keys", model_help: "Ex: claude-3-5-sonnet-latest, claude-opus-4-1" },
  { key: "gemini", label: "Google Gemini", model_default: "gemini-2.0-flash-exp", get_key_url: "https://aistudio.google.com/apikey", model_help: "Ex: gemini-2.0-flash-exp, gemini-2.5-pro" },
  { key: "openai", label: "OpenAI", model_default: "gpt-4o-mini", get_key_url: "https://platform.openai.com/api-keys", model_help: "Ex: gpt-4o-mini, gpt-4o, gpt-5" },
];

const ROLES = [
  { key: "admin", label: "Admin", hint: "Gerencia tudo, inclusive membros e cobrança" },
  { key: "editor", label: "Editor / Social", hint: "Cria e edita posicionamento, conteúdo e calendário" },
  { key: "aprovador", label: "Cliente / Aprovador", hint: "Vê e aprova conteúdo (leitura)" },
  { key: "viewer", label: "Viewer", hint: "Somente leitura" },
];

const TABS = [
  { key: "perfil", label: "Perfil", icon: User },
  { key: "seguranca", label: "Segurança", icon: Shield },
  { key: "time", label: "Time", icon: Users },
  { key: "ia", label: "IA & Integrações", icon: Sparkles },
  { key: "dados", label: "Controles & Dados", icon: Database },
];

// ----------------------------- PROFILE -----------------------------
function ProfileSection() {
  const { user, refresh } = useAuth();
  const { theme, setTheme, themes } = useTheme();
  const [name, setName] = useState(user?.name || "");
  const [picture, setPicture] = useState(user?.picture || "");
  const [saving, setSaving] = useState(false);
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const isGoogle = user?.auth_provider === "google";

  useEffect(() => {
    setName(user?.name || "");
    setPicture(user?.picture || "");
  }, [user]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put("/auth/profile", { name, picture, theme });
      if (refresh) await refresh();
      toast.success("Perfil atualizado");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const changePassword = async () => {
    if (pwd.next.length < 6) { toast.error("Senha nova precisa ter ao menos 6 caracteres"); return; }
    if (pwd.next !== pwd.confirm) { toast.error("Nova senha e confirmação não conferem"); return; }
    setPwSaving(true);
    try {
      await api.put("/auth/password", { current_password: pwd.current, new_password: pwd.next });
      toast.success("Senha alterada");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setPwSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="card p-6" data-testid="profile-section">
        <div className="flex items-center gap-4 mb-5">
          {picture ? (
            <img src={picture} alt="" className="w-16 h-16 rounded-full object-cover" />
          ) : (
            <div className="w-16 h-16 rounded-full grad-bg flex items-center justify-center text-white text-xl font-bold">{(name || "?").slice(0, 1).toUpperCase()}</div>
          )}
          <div className="flex-1">
            <div className="text-sm text-muted">{user?.email}</div>
            <div className="text-xs text-muted mt-1">Provedor: {isGoogle ? "Google" : "E-mail"}</div>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-xs text-muted font-mono uppercase tracking-widest">Nome</label>
            <input className="input mt-1.5" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" />
          </div>
          <div>
            <label className="text-xs text-muted font-mono uppercase tracking-widest">Foto (URL)</label>
            <input className="input mt-1.5" placeholder="https://..." value={picture} onChange={(e) => setPicture(e.target.value)} data-testid="profile-picture" />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 text-xs text-muted">
            <Palette size={14} />
            <span className="font-mono uppercase tracking-widest">Tema padrão:</span>
            <div className="card-2 px-1 py-1 flex items-center gap-1">
              {themes.map((t) => (
                <button key={t} onClick={() => setTheme(t)} className={`px-2 py-0.5 rounded text-xs ${theme === t ? "grad-bg text-white" : "text-muted"}`} data-testid={`theme-pick-${t}`}>
                  {({ principal: "◐ Principal", claro: "☀ Claro", escuro: "☾ Escuro" })[t]}
                </button>
              ))}
            </div>
          </div>
          <button onClick={saveProfile} disabled={saving} className="btn-grad text-sm flex items-center gap-2" data-testid="save-profile"><Save size={14} /> Salvar perfil</button>
        </div>
      </div>

      {/* Password (email accounts) */}
      <div className="card p-6" data-testid="password-section">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound size={16} className="grad-text" />
          <h3 className="font-bold">Senha</h3>
        </div>
        {isGoogle ? (
          <p className="text-sm text-muted">Sua conta é <strong>gerenciada pelo Google</strong>. A senha é feita no Google.</p>
        ) : (
          <div className="grid gap-3 md:grid-cols-3">
            <div>
              <label className="text-xs text-muted font-mono uppercase tracking-widest">Senha atual</label>
              <input type="password" className="input mt-1.5" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} data-testid="pwd-current" />
            </div>
            <div>
              <label className="text-xs text-muted font-mono uppercase tracking-widest">Nova senha</label>
              <input type="password" className="input mt-1.5" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} data-testid="pwd-next" />
            </div>
            <div>
              <label className="text-xs text-muted font-mono uppercase tracking-widest">Confirmar</label>
              <input type="password" className="input mt-1.5" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} data-testid="pwd-confirm" />
            </div>
            <div className="md:col-span-3">
              <button onClick={changePassword} disabled={pwSaving || !pwd.current || !pwd.next} className="btn-grad text-sm" data-testid="save-password">{pwSaving ? "Salvando..." : "Alterar senha"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ----------------------------- SECURITY / 2FA -----------------------------
function SecuritySection() {
  const { user } = useAuth();
  const [status, setStatus] = useState({ enabled: false, provider: "email", backup_codes_remaining: 0 });
  const [setup, setSetup] = useState(null); // {secret, qr_png_base64, otpauth_uri}
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [disablePwd, setDisablePwd] = useState("");
  const isGoogle = user?.auth_provider === "google";

  const load = async () => {
    try {
      const r = await api.get("/auth/2fa/status");
      setStatus(r.data);
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/setup");
      setSetup(r.data);
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const confirmEnable = async () => {
    if (!code || code.length !== 6) { toast.error("Digite o código de 6 dígitos"); return; }
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/enable", { code });
      setBackupCodes(r.data.backup_codes);
      setSetup(null);
      setCode("");
      toast.success("2FA ativado! Guarde os códigos de backup.");
      await load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const disable = async () => {
    if (!disablePwd) { toast.error("Digite sua senha para confirmar"); return; }
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password: disablePwd });
      toast.success("2FA desativado");
      setDisablePwd("");
      setBackupCodes(null);
      await load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (isGoogle) {
    return (
      <div className="card p-6" data-testid="security-google-notice">
        <div className="flex items-center gap-2 mb-2">
          <Shield size={16} className="grad-text" />
          <h3 className="font-bold">2FA (verificação em dois passos)</h3>
        </div>
        <p className="text-sm text-muted">Sua conta Google já tem 2FA gerenciada pelo Google. Ative na sua conta Google (accounts.google.com &rarr; Segurança).</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="security-section">
      <div className="card p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Shield size={16} className="grad-text" />
            <h3 className="font-bold">Verificação em dois passos (TOTP)</h3>
            {status.enabled && <span className="chip grad-bg text-white text-[10px]" style={{ padding: "2px 8px" }}>ATIVA</span>}
          </div>
          {!status.enabled && !setup && (
            <button onClick={startSetup} disabled={busy} className="btn-grad text-sm flex items-center gap-2" data-testid="totp-start"><Shield size={14} /> Ativar 2FA</button>
          )}
        </div>
        <p className="text-sm text-muted mt-2">Use Google Authenticator, 1Password, Authy ou similar. Ao logar, você vai precisar do código de 6 dígitos.</p>

        {setup && (
          <div className="mt-5 grid gap-4 md:grid-cols-2" data-testid="totp-setup">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted mb-2">// 1. Escaneie o QR</div>
              <img src={`data:image/png;base64,${setup.qr_png_base64}`} alt="QR 2FA" className="w-56 h-56 bg-white rounded p-2" data-testid="totp-qr" />
              <div className="text-[11px] text-muted mt-2">Ou insira o segredo manualmente:</div>
              <code className="text-xs card-2 p-2 mt-1 block break-all">{setup.secret}</code>
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted mb-2">// 2. Digite o código do app</div>
              <input
                className="input text-center text-xl tracking-widest"
                placeholder="000000"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                data-testid="totp-confirm-input"
              />
              <button onClick={confirmEnable} disabled={busy || code.length !== 6} className="btn-grad text-sm w-full mt-3" data-testid="totp-confirm-btn">
                Confirmar & ativar
              </button>
              <button onClick={() => setSetup(null)} className="btn-outline text-xs w-full mt-2">Cancelar</button>
            </div>
          </div>
        )}

        {backupCodes && (
          <div className="mt-5 card-2 p-4 border-l-4 border-[var(--warning)]" data-testid="backup-codes">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={14} className="text-[var(--warning)]" />
              <div className="font-bold text-sm">Códigos de backup (guarde agora!)</div>
            </div>
            <p className="text-xs text-muted mb-3">Cada código funciona uma vez. Use se perder o app.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {backupCodes.map((c) => (
                <code key={c} className="text-xs card p-2 text-center">{c}</code>
              ))}
            </div>
            <button
              onClick={() => { navigator.clipboard.writeText(backupCodes.join("\n")); toast.success("Copiado!"); }}
              className="btn-outline text-xs mt-3 flex items-center gap-1"
              data-testid="copy-backup-codes"
            >
              <Copy size={12} /> Copiar todos
            </button>
          </div>
        )}

        {status.enabled && !setup && (
          <div className="mt-5 card-2 p-4">
            <div className="text-sm mb-2">Códigos de backup restantes: <strong>{status.backup_codes_remaining}</strong></div>
            <div className="flex items-end gap-2 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs text-muted font-mono uppercase tracking-widest">Senha para desativar</label>
                <input type="password" className="input mt-1.5" value={disablePwd} onChange={(e) => setDisablePwd(e.target.value)} data-testid="totp-disable-pwd" />
              </div>
              <button onClick={disable} disabled={busy || !disablePwd} className="btn-outline text-sm text-[var(--error)]" data-testid="totp-disable-btn">Desativar 2FA</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ----------------------------- TEAM -----------------------------
function TeamSection() {
  const { user } = useAuth();
  const [data, setData] = useState({ members: [], invites: [], org_id: "" });
  const [invite, setInvite] = useState({ email: "", role: "editor" });
  const [busy, setBusy] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/team/members");
      setData(r.data);
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const myRole = data.members.find((m) => m.user_id === user?.user_id)?.role;
  const isAdmin = myRole === "admin";

  const doInvite = async () => {
    if (!invite.email) { toast.error("Informe o e-mail"); return; }
    setBusy(true);
    try {
      const r = await api.post("/team/invite", invite);
      const acceptLink = `${window.location.origin}/invite/${r.data.token}`;
      setLastInvite({ ...r.data, link: acceptLink });
      setInvite({ email: "", role: "editor" });
      toast.success("Convite gerado — copie o link");
      await load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const removeMember = async (uid) => {
    if (!window.confirm("Remover este membro?")) return;
    try {
      await api.delete(`/team/members/${uid}`);
      toast.success("Removido");
      await load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  const changeRole = async (uid, role) => {
    try {
      await api.put(`/team/members/${uid}/role`, { role });
      toast.success("Papel atualizado");
      await load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  const cancelInvite = async (invite_id) => {
    try {
      await api.delete(`/team/invites/${invite_id}`);
      toast.success("Convite cancelado");
      await load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="team-section">
      {isAdmin && (
        <div className="card p-6">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus size={16} className="grad-text" />
            <h3 className="font-bold">Convidar membro</h3>
          </div>
          <div className="grid gap-3 md:grid-cols-[2fr_1fr_auto] items-end">
            <div>
              <label className="text-xs text-muted font-mono uppercase tracking-widest">E-mail</label>
              <input className="input mt-1.5" type="email" placeholder="pessoa@empresa.com" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} data-testid="invite-email" />
            </div>
            <div>
              <label className="text-xs text-muted font-mono uppercase tracking-widest">Papel</label>
              <select className="input mt-1.5" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })} data-testid="invite-role">
                {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </div>
            <button onClick={doInvite} disabled={busy || !invite.email} className="btn-grad text-sm flex items-center gap-2" data-testid="invite-submit"><UserPlus size={14} /> Convidar</button>
          </div>
          <div className="text-[11px] text-muted mt-2">{ROLES.find((r) => r.key === invite.role)?.hint}</div>

          {lastInvite && (
            <div className="mt-4 card-2 p-3" data-testid="invite-link-box">
              <div className="text-xs text-muted mb-2">Link de convite (sem envio de e-mail configurado — copie e envie ao convidado):</div>
              <div className="flex items-center gap-2">
                <code className="text-xs flex-1 break-all card p-2">{lastInvite.link}</code>
                <button onClick={() => { navigator.clipboard.writeText(lastInvite.link); toast.success("Copiado!"); }} className="btn-outline text-xs flex items-center gap-1" data-testid="copy-invite-link"><Copy size={12} /> Copiar</button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="card overflow-hidden" data-testid="members-list">
        <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div className="font-bold">Membros</div>
          <div className="text-xs text-muted">Org: <code>{data.org_id}</code></div>
        </div>
        {data.members.map((m) => (
          <div key={m.user_id} className="grid grid-cols-[1fr_auto] gap-3 px-4 py-3 border-b items-center" style={{ borderColor: "var(--border)" }} data-testid={`member-${m.user_id}`}>
            <div className="flex items-center gap-3 min-w-0">
              {m.picture ? <img src={m.picture} alt="" className="w-9 h-9 rounded-full" /> : <div className="w-9 h-9 rounded-full grad-bg flex items-center justify-center text-white font-bold">{(m.name || "?").slice(0, 1).toUpperCase()}</div>}
              <div className="min-w-0">
                <div className="font-semibold truncate">{m.name}{m.user_id === user?.user_id && <span className="text-xs text-muted ml-2">(você)</span>}</div>
                <div className="text-xs text-muted truncate">{m.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isAdmin && m.user_id !== user?.user_id ? (
                <>
                  <select className="input py-1 text-xs" value={m.role} onChange={(e) => changeRole(m.user_id, e.target.value)} data-testid={`role-${m.user_id}`}>
                    {ROLES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                  </select>
                  <button onClick={() => removeMember(m.user_id)} className="text-muted hover:text-[var(--error)]" title="Remover" data-testid={`remove-${m.user_id}`}><X size={16} /></button>
                </>
              ) : (
                <span className="chip text-xs">{ROLES.find((r) => r.key === m.role)?.label || m.role}</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {data.invites.length > 0 && (
        <div className="card overflow-hidden" data-testid="pending-invites">
          <div className="px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
            <div className="font-bold">Convites pendentes</div>
          </div>
          {data.invites.map((inv) => (
            <div key={inv.invite_id} className="grid grid-cols-[1fr_auto_auto] gap-3 px-4 py-2 border-b items-center" style={{ borderColor: "var(--border)" }}>
              <div>
                <div className="text-sm">{inv.email}</div>
                <div className="text-xs text-muted">papel: {ROLES.find((r) => r.key === inv.role)?.label || inv.role}</div>
              </div>
              <button
                onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/invite/${inv.token}`); toast.success("Link copiado"); }}
                className="btn-outline text-xs flex items-center gap-1"
              >
                <Copy size={12} /> Link
              </button>
              {isAdmin && (
                <button onClick={() => cancelInvite(inv.invite_id)} className="text-muted hover:text-[var(--error)]"><X size={16} /></button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ----------------------------- IA & Integrações -----------------------------
function IntegrationsSection() {
  const { activeBrand, refresh } = useAuth();
  const [llm, setLlm] = useState({ provider: "anthropic", api_key: "", model: "" });
  const [current, setCurrent] = useState({ has_key: false });
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [labrand, setLabrand] = useState({ api_base: "", api_key: "" });
  const [labrandStatus, setLabrandStatus] = useState({ configured: false });
  const [showLabrandAdvanced, setShowLabrandAdvanced] = useState(false);
  const [testResult, setTestResult] = useState(null); // {status, text}
  const [labrandBrands, setLabrandBrands] = useState(null); // null | []
  const [loadingLabrand, setLoadingLabrand] = useState(false);
  const [importing, setImporting] = useState(false);
  const [resyncing, setResyncing] = useState(false);

  useEffect(() => {
    api.get("/llm/config").then((r) => {
      setCurrent(r.data);
      if (r.data.provider) setLlm((s) => ({ ...s, provider: r.data.provider, model: r.data.model || "" }));
    });
    api.get("/labrand/status").then((r) => setLabrandStatus(r.data));
  }, []);

  const providerConf = PROVIDERS.find((p) => p.key === llm.provider) || PROVIDERS[0];

  const saveLlm = async () => {
    if (!llm.api_key) { toast.error("Cole sua API key"); return; }
    setSaving(true);
    try {
      await api.put("/llm/config", { provider: llm.provider, api_key: llm.api_key, model: llm.model || providerConf.model_default });
      const { data } = await api.get("/llm/config");
      setCurrent(data);
      setLlm({ ...llm, api_key: "" });
      toast.success("Chave salva e criptografada!");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const clearLlm = async () => {
    if (!window.confirm("Remover chave de IA?")) return;
    try {
      await api.delete("/llm/config");
      const { data } = await api.get("/llm/config");
      setCurrent(data);
      toast.success("Chave removida");
    } catch { toast.error("Erro"); }
  };

  const testLlm = async () => {
    setTestResult({ status: "loading", text: "" });
    try {
      const { data } = await api.post("/ai/assist", { prompt: "Diga em 1 frase curta que a integração está funcionando.", max_tokens: 60 });
      setTestResult({ status: "ok", text: data.text });
      toast.success("Funcionando!");
    } catch (err) {
      setTestResult({ status: "err", text: fmtErr(err.response?.data?.detail) || "Erro ao testar" });
    }
  };

  const saveLabrand = async () => {
    if (!labrand.api_base || !labrand.api_key) { toast.error("Preencha URL e chave"); return; }
    try {
      await api.put("/labrand/connect", labrand);
      const { data } = await api.get("/labrand/status");
      setLabrandStatus(data);
      setLabrand({ ...labrand, api_key: "" });
      toast.success("LABrand conectado!");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  const loadLabrandBrands = async () => {
    setLoadingLabrand(true);
    try {
      const { data } = await api.get("/labrand/brands");
      setLabrandBrands(Array.isArray(data) ? data : (data?.brands || []));
    } catch (err) {
      const msg = fmtErr(err.response?.data?.detail) || "Não foi possível conectar ao LABrand. Confira a URL e a chave em Configuração avançada.";
      toast.error(msg);
      // Manter null pra continuar mostrando o botão principal (não parece que a conta está vazia)
      setLabrandBrands(null);
    } finally { setLoadingLabrand(false); }
  };

  const linkToActive = async (labrandBrandId, brandName) => {
    if (!activeBrand) { toast.error("Não há marca ativa"); return; }
    if (!window.confirm(`Vincular a marca ativa "${activeBrand.name}" à marca "${brandName}" do LABrand? O posicionamento atual será substituído pelo do LABrand.`)) return;
    setImporting(true);
    try {
      await api.post(`/brands/${activeBrand.brand_id}/link-labrand`, { labrand_brand_id: labrandBrandId, overwrite_positioning: true });
      if (refresh) await refresh();
      toast.success(`Marca vinculada! Modo trocado para "integrado ao LABrand".`);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Não foi possível vincular a marca. Tente novamente.");
    }
    finally { setImporting(false); }
  };

  const importAsNew = async (labrandBrandId) => {
    if (!window.confirm("Importar como uma NOVA marca no Social Booster?")) return;
    setImporting(true);
    try {
      const { data } = await api.post(`/labrand/import/${labrandBrandId}`);
      if (refresh) await refresh();
      toast.success(`Marca "${data.name}" importada e ativada!`);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Não foi possível importar. Tente novamente.");
    }
    finally { setImporting(false); }
  };

  const unlink = async () => {
    if (!activeBrand || activeBrand.source !== "labrand") return;
    if (!window.confirm("Desvincular do LABrand? A marca volta para modo manual (dados são mantidos).")) return;
    try {
      await api.post(`/brands/${activeBrand.brand_id}/unlink-labrand`);
      if (refresh) await refresh();
      toast.success("Marca voltou para modo manual");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Não foi possível desvincular.");
    }
  };

  const resync = async () => {
    if (!activeBrand || activeBrand.source !== "labrand") return;
    setImporting(true);
    try {
      const { data } = await api.post(`/brands/${activeBrand.brand_id}/resync-labrand`);
      if (refresh) await refresh();
      const when = data?.synced_at ? new Date(data.synced_at).toLocaleString("pt-BR") : "agora";
      toast.success(`Sincronizado com o LABrand (${when})`);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Não foi possível ressincronizar. Tente novamente.");
    } finally { setImporting(false); }
  };

  return (
    <div className="space-y-6">
      <div className="card p-6 space-y-4" data-testid="llm-section">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2"><Sparkles size={18} className="grad-text" /><h2 className="text-lg font-bold">Assistentes de IA</h2></div>
            <p className="text-sm text-muted mt-1">Sua chave. Sua conta. Fica criptografada no banco (Fernet AES).</p>
          </div>
          {current.has_key && <div className="chip grad-bg text-white flex items-center gap-1"><CircleCheckBig size={12} /> ativo</div>}
        </div>
        <div className="grid grid-cols-3 gap-2">
          {PROVIDERS.map((p) => (
            <button key={p.key} onClick={() => setLlm({ ...llm, provider: p.key, model: p.model_default })} className={`card-2 p-3 text-left transition ${llm.provider === p.key ? "ring-2 ring-[#C05BFF]" : ""}`} data-testid={`provider-${p.key}`}>
              <div className="font-semibold text-sm">{p.label}</div>
              <div className="text-[10px] text-muted mt-1 truncate">{p.model_default}</div>
            </button>
          ))}
        </div>
        <div>
          <label className="text-xs text-muted font-mono uppercase tracking-widest">API Key</label>
          <div className="mt-1.5 flex gap-2">
            <div className="flex-1 relative">
              <input className="input pr-10" type={showKey ? "text" : "password"} placeholder={current.has_key ? "•••••••• (chave já configurada — cole nova para substituir)" : "cole sua chave aqui"} value={llm.api_key} onChange={(e) => setLlm({ ...llm, api_key: e.target.value })} data-testid="llm-key" />
              <button type="button" onClick={() => setShowKey(!showKey)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted">{showKey ? <EyeOff size={16} /> : <Eye size={16} />}</button>
            </div>
          </div>
          <p className="text-xs text-muted mt-1">Obtenha em: <a href={providerConf.get_key_url} target="_blank" rel="noreferrer" className="grad-text hover:underline">{providerConf.get_key_url}</a></p>
        </div>
        <div>
          <label className="text-xs text-muted font-mono uppercase tracking-widest">Modelo (opcional)</label>
          <input className="input mt-1.5" placeholder={providerConf.model_default} value={llm.model} onChange={(e) => setLlm({ ...llm, model: e.target.value })} data-testid="llm-model" />
          <p className="text-xs text-muted mt-1">{providerConf.model_help}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={saveLlm} disabled={saving || !llm.api_key} className="btn-grad text-sm flex items-center gap-2" data-testid="save-llm"><Save size={14} /> Salvar chave</button>
          {current.has_key && (
            <>
              <button onClick={testLlm} className="btn-outline text-sm" data-testid="test-llm">Testar conexão</button>
              <button onClick={clearLlm} className="btn-outline text-sm text-[var(--error)]" data-testid="clear-llm"><Trash2 size={14} className="inline mr-1" /> Remover</button>
            </>
          )}
        </div>
        {testResult && (
          <div className={`card-2 p-3 border-l-4 ${testResult.status === "err" ? "border-[var(--error)]" : "border-[var(--success)]"}`} data-testid="llm-test-result">
            <div className="flex items-center justify-between mb-1">
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {testResult.status === "loading" ? "// testando…" : testResult.status === "err" ? "// erro" : "// resposta da IA"}
              </span>
              <button onClick={() => setTestResult(null)} className="text-muted hover:text-white"><X size={12} /></button>
            </div>
            <p className="text-sm">{testResult.status === "loading" ? "Enviando prompt de teste…" : testResult.text}</p>
          </div>
        )}
      </div>

      <div className="card p-6 space-y-4" data-testid="labrand-section">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2"><Link2 size={18} className="grad-text" /><h2 className="text-lg font-bold">Integração LABrand</h2></div>
            <p className="text-sm text-muted mt-1">Puxe estratégia de marca do LABrand automaticamente.</p>
          </div>
          {labrandStatus.configured && <div className="chip grad-bg text-white flex items-center gap-1"><CircleCheckBig size={12} /> conectado</div>}
        </div>

        {activeBrand && (
          <div className="card-2 p-3" data-testid="active-brand-mode">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-sm">
                <span className="text-muted">Marca ativa:</span>{" "}
                <strong>{activeBrand.name}</strong>{" "}
                <span className={`chip text-[10px] ml-1 ${activeBrand.source === "labrand" ? "grad-bg text-white" : ""}`} style={{ padding: "2px 8px" }}>
                  {activeBrand.source === "labrand" ? "integrado ao LABrand" : "manual"}
                </span>
              </div>
              {activeBrand.source === "labrand" && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={resync}
                    disabled={resyncing}
                    className="btn-grad text-xs flex items-center gap-1"
                    data-testid="labrand-resync-btn"
                    title="Puxar mudanças recentes do LABrand sem desvincular"
                  >
                    {resyncing ? <><Loader2Icon /> Sincronizando…</> : <>↻ Ressincronizar</>}
                  </button>
                  <button onClick={unlink} className="btn-outline text-xs text-[var(--error)]" data-testid="labrand-unlink-btn">Desvincular</button>
                </div>
              )}
            </div>
            {activeBrand.source === "labrand" && activeBrand.synced_at && (
              <div className="text-xs mt-2 font-mono" style={{ color: "var(--text-muted-strong, #b7afc3)" }}>
                <span className="opacity-60">// última sincronização:</span>{" "}
                <span className="opacity-95">{new Date(activeBrand.synced_at).toLocaleString("pt-BR")}</span>
              </div>
            )}
          </div>
        )}

        {labrandStatus.configured && (
          <div className="pt-2">
            {!labrandBrands ? (
              <button onClick={loadLabrandBrands} disabled={loadingLabrand} className="btn-grad text-sm flex items-center gap-2" data-testid="labrand-load-brands">
                {loadingLabrand ? <Loader2Icon /> : <Link2 size={14} />} Importar informações do LABrand
              </button>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted">// suas marcas no LABrand ({labrandBrands.length})</div>
                  <button onClick={loadLabrandBrands} className="text-xs text-muted hover:text-white">↻ atualizar</button>
                </div>
                {labrandBrands.length === 0 && <div className="text-sm text-muted py-4 text-center">Nenhuma marca encontrada no LABrand.</div>}
                <div className="space-y-2">
                  {labrandBrands.map((lb) => (
                    <div key={lb.brand_id || lb.id} className="card-2 p-3 flex items-center justify-between gap-2" data-testid={`labrand-brand-${lb.brand_id || lb.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate">{lb.name || "Sem nome"}</div>
                        <div className="text-[10px] text-muted truncate">{lb.industry || lb.handle || lb.brand_id || lb.id}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        {activeBrand && activeBrand.source !== "labrand" && (
                          <button
                            onClick={() => linkToActive(lb.brand_id || lb.id, lb.name)}
                            disabled={importing}
                            className="btn-grad text-xs flex items-center gap-1"
                            data-testid={`link-to-active-${lb.brand_id || lb.id}`}
                            title={`Substituir posicionamento de "${activeBrand.name}" pelo do LABrand e trocar modo`}
                          >
                            {importing ? <Loader2Icon /> : "Vincular a marca ativa"}
                          </button>
                        )}
                        <button
                          onClick={() => importAsNew(lb.brand_id || lb.id)}
                          disabled={importing}
                          className="btn-outline text-xs"
                          data-testid={`import-as-new-${lb.brand_id || lb.id}`}
                        >
                          + Nova marca
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={() => setShowLabrandAdvanced((s) => !s)}
          className="text-xs text-muted hover:text-white flex items-center gap-1"
          data-testid="labrand-advanced-toggle"
        >
          {showLabrandAdvanced ? "▾" : "▸"} Configuração avançada
        </button>
        {showLabrandAdvanced && (
          <>
            <div>
              <label className="text-xs text-muted font-mono uppercase tracking-widest">URL base da API</label>
              <input className="input mt-1.5" placeholder="https://seu-labrand.emergentagent.com/api" value={labrand.api_base} onChange={(e) => setLabrand({ ...labrand, api_base: e.target.value })} data-testid="labrand-base" />
            </div>
            <div>
              <label className="text-xs text-muted font-mono uppercase tracking-widest">API Key</label>
              <input type="password" className="input mt-1.5" placeholder={labrandStatus.configured ? "•••••••• (nova chave substitui)" : ""} value={labrand.api_key} onChange={(e) => setLabrand({ ...labrand, api_key: e.target.value })} data-testid="labrand-key" />
            </div>
            <button onClick={saveLabrand} disabled={!labrand.api_base || !labrand.api_key} className="btn-grad text-sm flex items-center gap-2" data-testid="save-labrand"><Save size={14} /> Salvar conexão</button>
          </>
        )}
      </div>
    </div>
  );
}

// ----------------------------- DADOS / MARCAS / DANGER ZONE -----------------------------
function DataSection() {
  const { user, activeBrand, refresh } = useAuth();
  const [brands, setBrands] = useState([]);
  const [editing, setEditing] = useState({}); // { [brand_id]: newName }
  const [confirmDel, setConfirmDel] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/brands");
      setBrands(r.data);
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const rename = async (brand_id) => {
    const newName = editing[brand_id];
    if (!newName) return;
    try {
      await api.put(`/brands/${brand_id}`, { name: newName });
      toast.success("Renomeada");
      setEditing({ ...editing, [brand_id]: "" });
      await load();
      if (refresh) await refresh();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  const activate = async (brand_id) => {
    try {
      await api.post(`/brands/${brand_id}/activate`);
      if (refresh) await refresh();
      toast.success("Marca ativada");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  const deleteBrand = async (brand_id, name) => {
    const input = window.prompt(`Digite o nome da marca "${name}" para confirmar exclusão:`);
    if (input !== name) { if (input !== null) toast.error("Nome não confere"); return; }
    try {
      await api.delete(`/brands/${brand_id}`);
      toast.success("Marca excluída");
      await load();
      if (refresh) await refresh();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  const exportData = async () => {
    setBusy(true);
    try {
      const res = await api.get("/account/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url; a.download = `social-booster-export.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Exportado");
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const deleteAccount = async () => {
    if (confirmEmail !== user?.email) { toast.error("E-mail não confere"); return; }
    if (!window.confirm("Isto é permanente. Tem certeza absoluta?")) return;
    try {
      await api.delete("/account", { data: { confirm_email: confirmEmail } });
      toast.success("Conta excluída");
      localStorage.removeItem("sb_token");
      window.location.href = "/login";
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="data-section">
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="font-bold">Marcas</div>
          <div className="text-xs text-muted">Renomeie, defina ativa ou exclua.</div>
        </div>
        {brands.length === 0 && <div className="p-6 text-center text-sm text-muted">Nenhuma marca ainda.</div>}
        {brands.map((b) => (
          <div key={b.brand_id} className="grid grid-cols-[1fr_auto] gap-3 px-4 py-3 border-b items-center" style={{ borderColor: "var(--border)" }} data-testid={`brand-row-${b.brand_id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded grad-bg flex items-center justify-center text-white font-bold">{(b.name || "?").slice(0, 1).toUpperCase()}</div>
              <div className="min-w-0 flex-1">
                <input
                  className="input py-1 text-sm"
                  value={editing[b.brand_id] ?? b.name}
                  onChange={(e) => setEditing({ ...editing, [b.brand_id]: e.target.value })}
                  onBlur={() => editing[b.brand_id] && editing[b.brand_id] !== b.name && rename(b.brand_id)}
                  data-testid={`brand-name-${b.brand_id}`}
                />
                <div className="text-[11px] text-muted mt-0.5">source: {b.source} · {activeBrand?.brand_id === b.brand_id && <span className="grad-text font-bold">ATIVA</span>}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {activeBrand?.brand_id !== b.brand_id && <button onClick={() => activate(b.brand_id)} className="btn-outline text-xs" data-testid={`activate-${b.brand_id}`}>Ativar</button>}
              <button onClick={() => deleteBrand(b.brand_id, b.name)} className="text-muted hover:text-[var(--error)]" data-testid={`delete-brand-${b.brand_id}`}><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      <div className="card p-6" data-testid="lgpd-section">
        <div className="flex items-center gap-2 mb-2"><Download size={16} className="grad-text" /><h3 className="font-bold">LGPD — Exportar meus dados</h3></div>
        <p className="text-sm text-muted mb-3">Baixa um JSON com seu usuário, marcas, posicionamento, workspace, conteúdo, calendário, funil e performance.</p>
        <button onClick={exportData} disabled={busy} className="btn-grad text-sm flex items-center gap-2" data-testid="export-data-btn">
          <Download size={14} /> Exportar meus dados
        </button>
      </div>

      <div className="card p-6 border-l-4 border-[var(--error)]" data-testid="danger-zone">
        <div className="flex items-center gap-2 mb-2"><AlertTriangle size={16} className="text-[var(--error)]" /><h3 className="font-bold">Danger zone</h3></div>
        <p className="text-sm text-muted mb-3">Excluir conta apaga permanentemente todas as suas marcas, dados e memberships. Não é reversível.</p>
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs text-muted font-mono uppercase tracking-widest">Digite seu e-mail: <code>{user?.email}</code></label>
            <input className="input mt-1.5" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} placeholder={user?.email} data-testid="delete-account-confirm" />
          </div>
          <button onClick={deleteAccount} disabled={confirmEmail !== user?.email} className="btn-outline text-sm text-[var(--error)] disabled:opacity-40" data-testid="delete-account-btn">
            Excluir conta
          </button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------- MAIN -----------------------------
export default function Settings() {
  const [tab, setTab] = useState("perfil");

  return (
    <div className="p-6 lg:p-10 max-w-5xl" data-testid="settings-page">
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold mb-1">// preferências</div>
        <h1 className="text-3xl font-extrabold">Configurações</h1>
      </div>

      <div className="flex items-center gap-1 mb-6 card-2 p-1.5 inline-flex" data-testid="settings-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-2 rounded-md text-sm flex items-center gap-2 transition ${tab === t.key ? "grad-bg text-white" : "text-muted hover:text-white"}`}
              data-testid={`tab-${t.key}`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "perfil" && <ProfileSection />}
      {tab === "seguranca" && <SecuritySection />}
      {tab === "time" && <TeamSection />}
      {tab === "ia" && <IntegrationsSection />}
      {tab === "dados" && <DataSection />}
    </div>
  );
}
